import { api } from './client';

/**
 * Endpoints de la configuración de la INSTALACIÓN (des-hardcodeo multi-tenant).
 * Prefijo: /api/installation-config. La config completa es ADMIN; la vista `/crm`
 * (opciones de la ficha del lead) es para cualquier usuario autenticado.
 */

export interface CatalogModel {
  name: string;
  price: number;
  note?: string;
  /** Descripción de venta (1-3 frases). Va al PDF y al agente como referencia. */
  descripcion?: string;
  aliases?: string[];
  imagenes: string[];
}

export interface CrmEditableField {
  key: string;
  label: string;
  placeholder: string;
  ia?: string;
  tipo?: 'texto' | 'numero';
  opciones?: string[];
}

/** Centro de operación (base física): referencia en Tracking. */
export interface OperationCenter {
  nombre: string;
  lat: number;
  lng: number;
  /** Radio en km que define "estar en la base". */
  radioKm: number;
}

/** Enlaces de Richard para invitar a su web y sus redes (solo http/https). Vacíos hasta que él los dé. */
export interface EnlacesDelNegocio {
  web?: string;
  instagram?: string;
  facebook?: string;
  tiktok?: string;
}

/** El guion del motor sin IA. `trato` y `enlaces` son de la tanda 3. */
export interface GuionConfig {
  presentacion: string;
  franjasZoom: string[];
  trato?: 'usted' | 'tu';
  enlaces?: EnlacesDelNegocio;
  /** Tanda 5: los videos de presentación, uno por propósito. */
  videos?: Partial<Record<PropositoDeVideo, VideoDePresentacion>>;
}

/** Tanda 5: para qué quiere el cliente la propiedad; cada propósito tiene su video de presentación. */
export type PropositoDeVideo = 'inversion' | 'familia' | 'vacaciones' | 'generico';
/** `key` es la clave del storage (subirVideo); `texto` va con el video y puede llevar {{nombre}}. */
export interface VideoDePresentacion {
  key: string;
  texto?: string;
}

/** Ola C: la agenda del Zoom. Días 0 = domingo … 6 = sábado; horas 'HH:MM' de 24 h, de Miami. */
export interface AgendaConfig {
  zoomUrl: string;
  dias: number[];
  horas: string[];
  duracionMin: number;
}

export interface CrmConfig {
  leadStatusLabels: Record<string, string>;
  editableFields: CrmEditableField[];
  zonas: string[];
  tiposServicio: string[];
  /** Centros de operación (bases). Vacío = sin bases definidas. */
  centros: OperationCenter[];
  minimoPorZona?: Record<string, number>;
  guion?: GuionConfig | null;
  agenda?: AgendaConfig | null;
}

export interface MessagesConfig {
  aiFallback: string;
  survey: string;
  slaOverdueOwner: string;
  slaOverdueTechnician: string;
  jobNotCompletedOwner: string;
  appointmentReminder: string;
  technicianArrived: string;
  surveyBadAlertOwner: string;
  /** Respuesta automática al cliente por encuesta POSITIVA. Vars: {{cliente}}. */
  surveyThanksGood: string;
  /** Respuesta automática al cliente por QUEJA. Vars: {{cliente}}. */
  surveyThanksBad: string;
  /** Acuse cordial para respuesta neutral o no clasificable. Vars: {{cliente}}. */
  surveyThanksNeutral: string;
  /** Aviso al dueño cuando un lead escala a CALIENTE. Vars: {{cliente}} {{telefono}} {{modelo}} {{zona}} {{motivo}} {{siguiente}} {{enlace}}. */
  hotLeadOwner: string;
  /** Aviso al dueño al ponchar LLEGADA. Vars: {{tecnico}} {{cliente}} {{direccion}} {{zona}} {{hora}}. */
  tecnicoLlegoOwner: string;
  /** Aviso al dueño al ponchar TERMINÉ. Vars: {{tecnico}} {{cliente}} {{direccion}} {{zona}} {{hora}}. */
  tecnicoTerminoOwner: string;
  /** Aviso interno: el técnico reporta que el cliente NO llegó. Vars: {{tecnico}} {{cliente}} {{descripcion}} {{zona}}. */
  noShowOwner: string;
  /** Aviso al dueño cuando el guard de gasto corta al agente. Vars: {{alcance}} {{gastado}} {{tope}}. */
  spendCapReachedOwner: string;
}

export interface AgentPersona {
  name: string;
  description: string;
  model: string;
  maxTokens: number;
  temperature: number;
  systemPrompt: string;
}

/** Un modelo con su período de garantía, en MESES (Configuración → Trabajos). */
export interface ModeloCobertura {
  id: string;
  nombre: string;
  mesesCobertura: number;
}

/** Una forma de pago del cierre. Se apaga sin borrarla. */
export interface FormaPagoOpcion {
  id: string;
  nombre: string;
  activa: boolean;
}

/** Cómo se resolvió una garantía. `codigo` es lo que se guarda en el trabajo. */
export interface NivelResolucion {
  id: string;
  codigo: string;
  nombre: string;
  activo: boolean;
}

/** Por qué falló una garantía. */
export interface CausaRaiz {
  id: string;
  nombre: string;
  activo: boolean;
}

/** Catálogos y reglas del módulo Trabajos (portado de silvai-chat). */
export interface TrabajosConfig {
  /** Encendido: el técnico no cierra, la oficina valida el cobro. Apagado por defecto. */
  cierreConValidacion: boolean;
  modelos: ModeloCobertura[];
  formasPago: FormaPagoOpcion[];
  nivelesResolucion: NivelResolucion[];
  causasRaiz: CausaRaiz[];
}

/** Config completa de la instalación (vista/edición de ADMIN). */
export interface InstallationConfig {
  key: string;
  businessName: string;
  agent: AgentPersona;
  catalog: CatalogModel[];
  notionDatabaseId: string | null;
  crm: CrmConfig;
  messages: MessagesConfig;
  slaHours: number;
  currencyPrefix: string;
  /** Logo del negocio (clave de storage) — se sube aparte, no viaja en el PUT. */
  logoKey: string | null;
  /** PDF del catálogo pre-generado (clave de storage), o null si no existe aún. */
  catalogPdfKey: string | null;
  /** Instalación interna de Silva Notion. SOLO lectura: el PUT no lo acepta. */
  isInternal: boolean;
  /** El vocabulario de este negocio. Nace vacío. */
  glosario: Glosario;
  /** Catálogos y reglas del módulo Trabajos. Nacen vacíos. */
  trabajos: TrabajosConfig;
}

/** Un término del negocio, con lo que significa acá adentro. */
export interface TerminoGlosario {
  termino: string;
  significa: string;
  /** Cómo se llama el campo en la base, si aplica. */
  enLaBase?: string;
}

/** Algo que NO hay que decir, con su reemplazo. */
export interface TerminoAEvitar {
  no: string;
  si: string;
}

/** GLOSARIO DEL NEGOCIO (portado de silvai-chat). */
export interface Glosario {
  terminos: TerminoGlosario[];
  evitar: TerminoAEvitar[];
}

/** Vista para la ficha del lead (cualquier usuario autenticado). */
export interface CrmView {
  businessName: string;
  currencyPrefix: string;
  crm: CrmConfig;
  catalog: { name: string; price: number }[];
  /** Instalación interna de Silva Notion (habilita el selector de motor). */
  isInternal?: boolean;
  /** Catálogos de Trabajos: el técnico elige el modelo y la oficina la forma de pago. */
  trabajos?: TrabajosConfig;
}

export const getInstallationConfig = () =>
  api.get<InstallationConfig>('/api/installation-config');

export const saveInstallationConfig = (
  config: Omit<InstallationConfig, 'key' | 'logoKey' | 'catalogPdfKey' | 'isInternal'>,
) => api.put<InstallationConfig>('/api/installation-config', config);

export const getCrmView = () => api.get<CrmView>('/api/installation-config/crm');

/** Sube el logo del negocio (multipart). Devuelve la clave/URL guardadas. */
export const uploadLogo = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.post<{ key: string; url: string }>('/api/installation-config/logo', form);
};

/** Sube la foto de un producto del catálogo. Devuelve clave/URL para agregarla al modelo. */
export const uploadCatalogImage = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.post<{ key: string; url: string }>('/api/installation-config/catalog-image', form);
};

/** Regenera el PDF del catálogo (tras subir fotos nuevas, p. ej.). */
export const regenerateCatalogPdf = () =>
  api.post<{ catalogPdfKey: string; url: string }>('/api/installation-config/regenerate-pdf');

/**
 * Verifica en el servidor qué claves de imagen NO tienen archivo en el storage
 * (claves "fantasma"). Base de la acción "quitar fotos rotas" de una fila.
 */
export const checkCatalogImages = (keys: string[]) =>
  api.post<{ missing: string[] }>('/api/installation-config/catalog-images/check', { keys });
